import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, Plus } from 'lucide-react';
import { Button } from '@/components/Button';
import { DetailHeader } from '@/components/DetailHeader';
import { Spinner } from '@/components/Spinner';
import { TierLimitModal } from '@/components/TierLimitModal';
import { PrinterLibraryDetailView, PrinterForm } from '@/features/printers/components';
import { usePrinterLibraryItem, useAddPrinterToLibrary, useTenantPrinters } from '@/features/printers/hooks';
import { useTier } from '@/hooks/useAuth';
import { useToast } from '@/components/ToastProvider';
import type { AddPrinterToLibraryInput } from '@maker-toolchest/shared';
import { getEffectivePrinterLimit } from '@maker-toolchest/shared';

export function PrinterLibraryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { tier } = useTier();
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [isTierLimitModalOpen, setIsTierLimitModalOpen] = useState(false);
  const [addPrinterError, setAddPrinterError] = useState<Error | null>(null);

  const { data: printer, isLoading, error } = usePrinterLibraryItem(id!);
  const { data: printersData } = useTenantPrinters({
    page: 1,
    limit: 100,
    sortBy: 'addedAt',
    sortOrder: 'desc'
  });
  const addToLibrary = useAddPrinterToLibrary();

  const printers = printersData || [];

  // Get tier limits
  const printerLimit = tier ? getEffectivePrinterLimit(tier) : Infinity;
  const isAtPrinterLimit = printers.length >= printerLimit;

  const handleBack = () => {
    navigate('/printers');
  };

  const handleAddClick = () => {
    // Check tier limit before opening form
    if (isAtPrinterLimit) {
      setIsTierLimitModalOpen(true);
      return;
    }

    setAddPrinterError(null);
    setIsAddFormOpen(true);
  };

  const handleAddPrinter = async (data: AddPrinterToLibraryInput) => {
    try {
      setAddPrinterError(null);
      await addToLibrary.mutateAsync(data);
      setIsAddFormOpen(false);
      toast.success('Printer added successfully');
      navigate('/printers');
    } catch (error) {
      setAddPrinterError(error instanceof Error ? error : new Error('Failed to add printer'));
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !printer) {
    return (
      <div className="p-4">
        <div className="mb-4">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ArrowLeft className="h-4 w-4" />}
            onClick={handleBack}
          >
            Back to Printers
          </Button>
        </div>
        <div className="rounded-lg border border-border bg-background-subtle p-8 text-center">
          <p className="text-error">
            {error ? 'Failed to load printer details' : 'Printer not found'}
          </p>
          <Button variant="secondary" onClick={handleBack} className="mt-4">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // Build metadata for header
  const metadata = (
    <div className="flex items-center gap-2">
      <span className="rounded bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
        {printer.technology}
      </span>
      {printer.tenantId && (
        <span className="rounded bg-accent/10 px-2 py-1 text-xs font-medium text-accent">
          Custom
        </span>
      )}
    </div>
  );

  // Build actions for header
  const actions = (
    <Button
      variant="primary"
      size="sm"
      leftIcon={<Plus className="h-4 w-4" />}
      onClick={handleAddClick}
    >
      Add to Library
    </Button>
  );

  // Build description
  const description = printer.brand
    ? `${printer.brand.name}${printer.model ? ` · ${printer.model.name}` : ''}`
    : undefined;

  return (
    <div className="flex h-full flex-col">
      {/* Header with back button and actions */}
      <DetailHeader
        onBack={handleBack}
        backLabel="Back to Printers"
        icon={Printer}
        iconSize={12}
        title={printer.name}
        description={description}
        metadata={metadata}
        actions={actions}
      />

      {/* Detail view */}
      <div className="flex-1 overflow-y-auto">
        <PrinterLibraryDetailView printer={printer} />
      </div>

      {/* Add Printer Form */}
      <PrinterForm
        open={isAddFormOpen}
        onClose={() => {
          setIsAddFormOpen(false);
          setAddPrinterError(null);
        }}
        onSubmit={handleAddPrinter}
        printerId={id}
        loading={addToLibrary.isPending}
        error={addPrinterError}
      />

      {/* Tier Limit Modal */}
      <TierLimitModal
        open={isTierLimitModalOpen}
        onClose={() => setIsTierLimitModalOpen(false)}
        resourceName="printers"
        resourceLabel="Printers"
        currentCount={printers.length}
        maxCount={printerLimit}
        tierName={tier || 'Free'}
        message={`You cannot add more printers on the ${tier || 'Free'} tier. To add more than ${printerLimit}, upgrade your plan.`}
      />
    </div>
  );
}
